import { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Radio, Typography } from 'antd';
import type { TenantView } from './types';

export type ResetPasswordChoice = 'random' | 'explicit';

export interface ResetPasswordSubmitPayload {
  /** when choice='random' the password is null and the server generates one */
  password: string | null;
}

interface Props {
  tenant: TenantView | null;
  open: boolean;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: ResetPasswordSubmitPayload) => void;
}

interface FormValues {
  choice: ResetPasswordChoice;
  password?: string;
  confirmPassword?: string;
}

export function ResetPasswordModal({ tenant, open, submitting, onCancel, onSubmit }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [choice, setChoice] = useState<ResetPasswordChoice>('random');

  useEffect(() => {
    if (open) {
      form.resetFields();
      setChoice('random');
    }
  }, [open, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (values.choice === 'random') {
          onSubmit({ password: null });
        } else {
          onSubmit({ password: (values.password ?? '').trim() });
        }
      })
      .catch(() => {
        /* validation errors already shown inline */
      });
  };

  return (
    <Modal
      title={tenant ? `重置密码：${tenant.name}（${tenant.slug}）` : '重置密码'}
      open={open}
      onCancel={onCancel}
      destroyOnClose
      width={520}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Popconfirm
          key="confirm"
          title="确认执行密码重置？"
          description="原密码将立即失效，此操作不可撤销。"
          okText="确认重置"
          okButtonProps={{ danger: true, loading: submitting }}
          cancelText="取消"
          onConfirm={handleOk}
        >
          <Button type="primary" danger loading={submitting}>
            重置密码
          </Button>
        </Popconfirm>,
      ]}
    >
      <Typography.Paragraph type="secondary">
        重置后，原密码立即失效。新密码只会在下一步显示一次，请妥善保管。
      </Typography.Paragraph>
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={{ choice: 'random' }}
        onValuesChange={(changed) => {
          if (changed.choice) setChoice(changed.choice);
        }}
      >
        <Form.Item name="choice" label="新密码方式">
          <Radio.Group>
            <Radio.Button value="random">随机生成（推荐）</Radio.Button>
            <Radio.Button value="explicit">手动指定</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {choice === 'explicit' && (
          <>
            <Form.Item
              label="新密码"
              name="password"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 8, message: '至少 8 个字符' },
                { max: 64, message: '不超过 64 个字符' },
              ]}
            >
              <Input.Password autoComplete="new-password" placeholder="至少 8 位" />
            </Form.Item>
            <Form.Item
              label="确认密码"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
